'use client';

import { 
  ArrowLeft,
  Shield,
  Calendar,
  AlertCircle,
  CheckCircle,
  Info,
  Lock,
  Eye,
  Database,
  UserCheck
} from 'lucide-react';
import { Button } from '@myapp/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@myapp/ui/components/card';
import { Badge } from '@myapp/ui/components/badge';
import Link from 'next/link';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Link href="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                홈으로 돌아가기
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">개인정보 처리방침</h1>
              <p className="text-gray-600 mt-1">강원푸드핏의 개인정보 보호정책입니다</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Document Info */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Shield className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">강원푸드핏 개인정보 처리방침</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>시행일: 2025년 1월 1일</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    <span>버전: 1.0</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>최신 버전</span>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-800">
                    <Lock className="w-4 h-4 inline mr-1" />
                    회원님의 개인정보는 암호화되어 안전하게 보호됩니다.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Privacy Content */}
        <div className="space-y-8">
          {/* 제1조 */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl text-gray-900 flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-600" />
                제1조 (개인정보의 처리목적)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>주식회사 JHLAB(이하 "강원푸드핏")은 다음의 목적을 위하여 개인정보를 처리합니다:</p>
                <div className="grid gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                    <h4 className="font-semibold text-blue-900 mb-2">🛒 전자상거래 서비스 제공</h4>
                    <p className="text-blue-800">회원가입, 상품주문, 결제처리, 배송관리</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-400">
                    <h4 className="font-semibold text-green-900 mb-2">🤝 고객 서비스 제공</h4>
                    <p className="text-green-800">고객상담, 불만처리, 공지사항 전달</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg border-l-4 border-purple-400">
                    <h4 className="font-semibold text-purple-900 mb-2">🎯 맞춤형 서비스 제공</h4>
                    <p className="text-purple-800">AI 레시피 추천, 개인화된 상품 정보 제공</p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg border-l-4 border-orange-400">
                    <h4 className="font-semibold text-orange-900 mb-2">📊 서비스 개선 및 분석</h4>
                    <p className="text-orange-800">이용통계 분석, 서비스 품질 개선</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 제2조 */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl text-gray-900 flex items-center gap-2">
                <Database className="w-5 h-5 text-green-600" />
                제2조 (개인정보의 처리 및 보유기간)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>처리목적별 개인정보의 처리 및 보유기간은 다음과 같습니다:</p>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 px-4 py-3 text-left">처리목적</th>
                        <th className="border border-gray-300 px-4 py-3 text-left">보유기간</th>
                        <th className="border border-gray-300 px-4 py-3 text-left">관련 법령</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-gray-300 px-4 py-3">회원가입 및 관리</td>
                        <td className="border border-gray-300 px-4 py-3">회원탈퇴 후 즉시 삭제</td>
                        <td className="border border-gray-300 px-4 py-3">개인정보보호법</td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="border border-gray-300 px-4 py-3">전자상거래 서비스</td>
                        <td className="border border-gray-300 px-4 py-3">5년</td>
                        <td className="border border-gray-300 px-4 py-3">전자상거래법</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 px-4 py-3">결제 및 환불 관련</td>
                        <td className="border border-gray-300 px-4 py-3">5년</td>
                        <td className="border border-gray-300 px-4 py-3">국세기본법</td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="border border-gray-300 px-4 py-3">고객상담 기록</td>
                        <td className="border border-gray-300 px-4 py-3">3년</td>
                        <td className="border border-gray-300 px-4 py-3">소비자보호법</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 제3조 */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl text-gray-900 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-purple-600" />
                제3조 (개인정보의 처리 항목)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>다음과 같은 개인정보를 처리하고 있습니다:</p>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Badge className="bg-blue-100 text-blue-800">필수</Badge>
                      필수 수집 정보
                    </h4>
                    <ul className="list-disc list-inside ml-4 space-y-2">
                      <li><strong>회원가입:</strong> 이름, 이메일 주소, 전화번호, 비밀번호</li>
                      <li><strong>주문/배송:</strong> 배송지 주소, 수취인 정보</li>
                      <li><strong>결제:</strong> 결제수단 정보 (카드번호는 암호화 저장)</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Badge className="bg-gray-100 text-gray-800">선택</Badge>
                      선택 수집 정보
                    </h4>
                    <ul className="list-disc list-inside ml-4 space-y-2">
                      <li><strong>맞춤 서비스:</strong> 식품 알레르기 정보, 선호 음식</li>
                      <li><strong>마케팅:</strong> 생년월일, 성별 (동의 시에만)</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Badge className="bg-yellow-100 text-yellow-800">자동</Badge>
                      자동 수집 정보
                    </h4>
                    <ul className="list-disc list-inside ml-4 space-y-2">
                      <li><strong>서비스 이용:</strong> IP주소, 쿠키, 접속로그</li>
                      <li><strong>기기정보:</strong> 브라우저 종류, OS 정보</li>
                    </ul>
                  </div>
                </div>
                
                <div className="p-4 bg-amber-50 border-l-4 border-amber-400 rounded">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <p className="text-amber-800 text-sm">
                      <strong>중요:</strong> 필수 정보를 제공하지 않을 경우 서비스 이용에 제한이 있을 수 있습니다.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 제4조 */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl text-gray-900 flex items-center gap-2">
                <Eye className="w-5 h-5 text-red-600" />
                제4조 (개인정보의 제3자 제공)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p><strong>원칙적으로 개인정보를 제3자에게 제공하지 않습니다.</strong></p>
                <p>다만, 다음의 경우에는 예외로 합니다:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>이용자가 사전에 동의한 경우</li>
                  <li>법령의 규정에 의거하거나 수사목적으로 법령에 정해진 절차와 방법에 따라 수사기관의 요구가 있는 경우</li>
                </ul>
                
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-900 mb-3">📦 배송업체 정보 제공 (주문 시에만)</h4>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• <strong>제공받는 자:</strong> 배송업체 (CJ대한통운, 한진택배 등)</li>
                      <li>• <strong>제공 정보:</strong> 수취인명, 연락처, 주소</li>
                      <li>• <strong>제공 목적:</strong> 상품 배송</li>
                      <li>• <strong>보유기간:</strong> 배송 완료 후 30일</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 제5조 */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl text-gray-900 flex items-center gap-2">
                <Lock className="w-5 h-5 text-green-600" />
                제5조 (개인정보 보호를 위한 기술적 조치)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>개인정보를 안전하게 보호하기 위해 다음과 같은 기술적 조치를 취하고 있습니다:</p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-400">
                    <h4 className="font-semibold text-green-900 mb-2">🔒 암호화 조치</h4>
                    <ul className="text-sm text-green-800 space-y-1">
                      <li>• 비밀번호 단방향 암호화</li>
                      <li>• 결제정보 SSL 암호화</li>
                      <li>• 데이터베이스 암호화</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                    <h4 className="font-semibold text-blue-900 mb-2">🛡️ 접근 통제</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• 관리자 권한 최소화</li>
                      <li>• 접근 로그 모니터링</li>
                      <li>• VPN 보안 접속</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-purple-50 rounded-lg border-l-4 border-purple-400">
                    <h4 className="font-semibold text-purple-900 mb-2">🔄 백업 및 복구</h4>
                    <ul className="text-sm text-purple-800 space-y-1">
                      <li>• 정기적 데이터 백업</li>
                      <li>• 재해 복구 시스템</li>
                      <li>• 데이터 무결성 검증</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-orange-50 rounded-lg border-l-4 border-orange-400">
                    <h4 className="font-semibold text-orange-900 mb-2">🔍 보안 모니터링</h4>
                    <ul className="text-sm text-orange-800 space-y-1">
                      <li>• 24시간 보안 모니터링</li>
                      <li>• 침입탐지 시스템</li>
                      <li>• 보안 취약점 점검</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 제6조 */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl text-gray-900 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-blue-600" />
                제6조 (정보주체의 권리·의무 및 행사방법)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>이용자는 개인정보 주체로서 다음과 같은 권리를 행사할 수 있습니다:</p>
                
                <div className="grid gap-4">
                  <div className="p-4 bg-indigo-50 rounded-lg">
                    <h4 className="font-semibold text-indigo-900 mb-2">📋 열람권</h4>
                    <p className="text-indigo-800 text-sm">본인의 개인정보 처리현황을 확인할 권리</p>
                  </div>
                  
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h4 className="font-semibold text-green-900 mb-2">✏️ 정정·삭제권</h4>
                    <p className="text-green-800 text-sm">잘못된 정보의 수정이나 삭제를 요구할 권리</p>
                  </div>
                  
                  <div className="p-4 bg-red-50 rounded-lg">
                    <h4 className="font-semibold text-red-900 mb-2">⏸️ 처리정지권</h4>
                    <p className="text-red-800 text-sm">개인정보 처리를 중단하도록 요구할 권리</p>
                  </div>
                </div>
                
                <div className="p-4 bg-gray-100 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">권리 행사 방법</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• <strong>온라인:</strong> 마이페이지 &gt; 개인정보 관리</li>
                    <li>• <strong>이메일:</strong> privacy@gangwonfoodfit.co.kr</li>
                    <li>• <strong>전화:</strong> 033-248-1234 (평일 09:00-18:00)</li>
                    <li>• <strong>우편:</strong> 강원특별자치도 춘천시 한림대학길 1 JHLAB</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 제7조 */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl text-gray-900">제7조 (개인정보보호책임자)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>개인정보 처리에 관한 업무를 총괄해서 책임지고, 개인정보 처리와 관련한 정보주체의 불만처리 및 피해구제 등을 위하여 아래와 같이 개인정보보호책임자를 지정하고 있습니다.</p>
                
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-3">개인정보보호책임자</h4>
                      <ul className="text-blue-800 space-y-2">
                        <li><strong>성명:</strong> 김성호</li>
                        <li><strong>직책:</strong> CPO</li>
                        <li><strong>이메일:</strong> sungho22@hallym.ac.kr</li>
                        <li><strong>전화:</strong> 010-9646-2558</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-3">개인정보 담당부서</h4>
                      <ul className="text-blue-800 space-y-2">
                        <li><strong>부서명:</strong> 개인정보보호팀</li>
                        <li><strong>담당자:</strong> 개인정보보호팀</li>
                        <li><strong>이메일:</strong> privacy@gangwonfoodfit.co.kr</li>
                        <li><strong>전화:</strong> 033-248-1234</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <p>정보주체께서는 강원푸드핏의 서비스를 이용하시면서 발생한 모든 개인정보보호 관련 문의, 불만처리, 피해구제 등에 관한 사항을 개인정보보호책임자 및 담당부서로 문의하실 수 있습니다.</p>
              </div>
            </CardContent>
          </Card>

          {/* 제8조 */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl text-gray-900">제8조 (개인정보 처리방침의 변경)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p><strong>1.</strong> 이 개인정보 처리방침은 시행일로부터 적용되며, 법령 및 방침에 따른 변경내용의 추가, 삭제 및 정정이 있는 경우에는 변경사항의 시행 7일 전부터 공지사항을 통하여 고지할 것입니다.</p>
                <p><strong>2.</strong> 본 방침은 정부의 정책 또는 보안기술의 변화에 따라 내용의 추가·삭제 및 수정이 있을 시에는 개정 최소 7일 전에 홈페이지를 통해 변경이유 및 내용 등을 공지하도록 하겠습니다.</p>
                
                <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <p className="text-yellow-800 text-sm">
                      <strong>알림:</strong> 개인정보 처리방침 변경 시 이메일 또는 서비스 내 알림으로 안내드립니다.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 부칙 */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl text-gray-900">부칙</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p><strong>1.</strong> 본 개인정보 처리방침은 2025년 1월 1일부터 적용됩니다.</p>
                <p><strong>2.</strong> 이전의 개인정보 처리방침은 회사 홈페이지에서 확인할 수 있습니다.</p>
                
                <div className="pt-6 border-t border-gray-200">
                  <div className="text-center text-sm text-gray-500">
                    <p>주식회사 JHLAB | 개인정보보호책임자: 김성호</p>
                    <p>강원특별자치도 춘천시 한림대학길 1 | 사업자등록번호: 123-45-67890</p>
                    <p>최종 수정일: 2025년 1월 1일</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Navigation */}
        <div className="mt-12 flex justify-between">
          <Link href="/terms">
            <Button variant="outline">
              서비스 이용약관 보기
              <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
            </Button>
          </Link>
          <Link href="/">
            <Button>
              홈으로 돌아가기
              <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}